import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["messages", "input", "sendBtn", "typing", "typingNames", "reactionPicker"]
  static values = {
    id: Number,
    messagesUrl: String,
    currentGuest: Number
  }

  connect() {
    this.subscription = null
    this.typingTimeout = null
    this.isTyping = false
    this.typingUsers = new Set()

    this.connectToChannel()
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  connectToChannel() {
    const consumer = createConsumer()

    this.subscription = consumer.subscriptions.create(
      {
        channel: "ChatRoomChannel",
        chat_room_id: this.idValue
      },
      {
        connected: () => {
          console.log("Connected to chat room", this.idValue)
        },

        disconnected: () => {
          console.log("Disconnected from chat room")
        },

        received: (data) => {
          this.handleMessage(data)
        }
      }
    )
  }

  handleMessage(data) {
    switch (data.type) {
      case "message":
        this.appendMessage(data.message)
        break
      case "message_updated":
        this.replaceMessage(data.message)
        break
      case "typing":
        this.showTyping(data.guest_name)
        break
      case "stop_typing":
        this.hideTyping(data.guest_name)
        break
      case "reaction_added":
      case "reaction_removed":
        this.updateReactions(data.message_id, data.reactions)
        break
      case "read_receipt":
        // Could update read receipts here
        break
    }
  }

  appendMessage(message) {
    const typingEl = this.typingTarget
    typingEl.insertAdjacentElement("beforebegin", this.buildMessage(message))
    this.scrollToBottom()
  }

  replaceMessage(message) {
    const current = document.getElementById(`message-${message.id}`)
    if (current) current.replaceWith(this.buildMessage(message))
  }

  buildMessage(message) {
    if (message.message_type === "system") {
      const element = this.messageElement("div", "chat-system-message", message.id)
      element.textContent = message.content
      return element
    }

    if (message.message_type === "suggestion") {
      const element = this.messageElement("div", "chat-suggestion", message.id)
      const icon = document.createElement("div")
      icon.className = "chat-suggestion-icon"
      icon.textContent = "💡"
      const content = document.createElement("div")
      content.className = "chat-suggestion-content"
      content.textContent = message.content
      const meta = document.createElement("div")
      meta.className = "chat-message-meta"
      meta.style.cssText = "margin-top: 0.5rem; margin-left: 0;"
      meta.textContent = `${message.guest.name || "Sistema"} • ${message.time_ago}`
      element.append(icon, content, meta)
      return element
    }

    const isMine = Number(message.guest.id) === this.currentGuestValue
    const element = this.messageElement("div", `chat-message ${isMine ? "mine" : ""}`, message.id)
    const avatar = document.createElement("div")
    avatar.className = "chat-message-avatar"
    avatar.textContent = message.guest.name?.charAt(0).toUpperCase() || "?"

    const content = document.createElement("div")
    content.className = "chat-message-content"
    if (!isMine) {
      const sender = document.createElement("span")
      sender.className = "chat-message-sender"
      sender.textContent = message.guest.name?.split(" ")[0] || "Convidado"
      content.appendChild(sender)
    }

    const bubble = document.createElement("div")
    bubble.className = "chat-bubble"
    bubble.dataset.action = "dblclick->chat-room#showReactionPicker"
    if (message.reply_to) {
      const reply = document.createElement("div")
      reply.className = "chat-message-reply"
      const author = document.createElement("strong")
      author.textContent = `${message.reply_to.guest_name}: `
      reply.append(author, document.createTextNode(message.reply_to.content))
      bubble.appendChild(reply)
    }
    const body = document.createElement("div")
    body.style.whiteSpace = "pre-wrap"
    body.textContent = message.content
    bubble.appendChild(body)

    const meta = document.createElement("div")
    meta.className = "chat-message-meta"
    meta.textContent = this.messageTime(message.created_at)
    if (message.edited) meta.appendChild(document.createTextNode(" • editado"))

    content.append(bubble, meta)
    element.append(avatar, content)
    this.renderReactions(content, message.reactions)
    return element
  }

  messageElement(tagName, className, id) {
    const element = document.createElement(tagName)
    element.className = className
    element.id = `message-${id}`
    element.dataset.messageId = id
    return element
  }

  messageTime(createdAt) {
    return new Intl.DateTimeFormat("pt-PT", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).format(new Date(createdAt))
  }

  showTyping(guestName) {
    this.typingUsers.add(guestName)
    this.updateTypingIndicator()
  }

  hideTyping(guestName) {
    this.typingUsers.delete(guestName)
    this.updateTypingIndicator()
  }

  updateTypingIndicator() {
    if (this.typingUsers.size > 0) {
      this.typingTarget.classList.add("active")
      const names = Array.from(this.typingUsers)
      if (names.length === 1) {
        this.typingNamesTarget.textContent = `${names[0]} está a escrever...`
      } else if (names.length === 2) {
        this.typingNamesTarget.textContent = `${names[0]} e ${names[1]} estão a escrever...`
      } else {
        this.typingNamesTarget.textContent = `${names.length} pessoas estão a escrever...`
      }
    } else {
      this.typingTarget.classList.remove("active")
    }
  }

  updateReactions(messageId, reactions) {
    const messageEl = document.getElementById(`message-${messageId}`)
    if (!messageEl) return

    this.renderReactions(messageEl.querySelector(".chat-message-content"), reactions)
  }

  renderReactions(content, reactions) {
    if (!content) return

    let reactionsContainer = content.querySelector(".chat-message-reactions")

    if (reactions.length === 0) {
      if (reactionsContainer) reactionsContainer.remove()
      return
    }

    if (!reactionsContainer) {
      reactionsContainer = document.createElement("div")
      reactionsContainer.className = "chat-message-reactions"
      content.appendChild(reactionsContainer)
    }

    reactionsContainer.innerHTML = ""
    for (const reaction of reactions) {
      const reactionEl = document.createElement("span")
      const mine = reaction.guest_ids.map(Number).includes(this.currentGuestValue)
      reactionEl.className = `chat-reaction ${mine ? "mine" : ""}`
      reactionEl.dataset.emoji = reaction.emoji
      reactionEl.dataset.action = "click->chat-room#toggleReaction"
      reactionEl.textContent = `${reaction.emoji} ${reaction.count}`
      reactionsContainer.appendChild(reactionEl)
    }
  }

  onTyping() {
    if (!this.isTyping) {
      this.isTyping = true
      this.subscription.perform("typing")
    }

    clearTimeout(this.typingTimeout)
    this.typingTimeout = setTimeout(() => {
      this.isTyping = false
      this.subscription.perform("stop_typing")
    }, 2000)
  }

  onKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  sendMessage() {
    const content = this.inputTarget.value.trim()
    if (!content) return

    this.sendBtnTarget.disabled = true

    this.subscription.perform("send_message", {
      content: content,
      message_type: "text"
    })

    this.inputTarget.value = ""
    this.inputTarget.style.height = "auto"

    // Stop typing indicator
    if (this.isTyping) {
      this.isTyping = false
      this.subscription.perform("stop_typing")
    }

    setTimeout(() => {
      this.sendBtnTarget.disabled = false
      this.inputTarget.focus()
    }, 100)
  }

  loadMore() {
    const firstMessage = this.messagesTarget.querySelector("[data-message-id]")
    if (!firstMessage) return

    const beforeId = firstMessage.dataset.messageId

    const url = new URL(this.messagesUrlValue, window.location.origin)
    url.searchParams.set("before_id", beforeId)

    fetch(url)
      .then(response => response.json())
      .then(data => {
        const loadMoreBtn = this.messagesTarget.querySelector(".chat-load-more")

        if (data.messages.length > 0) {
          // Insert messages before the first message
          data.messages.reverse().forEach(message => {
            firstMessage.insertAdjacentElement("beforebegin", this.buildMessage(message))
          })
        }

        if (!data.has_more && loadMoreBtn) {
          loadMoreBtn.remove()
        }
      })
  }

  showReactionPicker(event) {
    const messageEl = event.target.closest(".chat-message")
    if (!messageEl) return

    this.selectedMessageId = messageEl.dataset.messageId

    const picker = this.reactionPickerTarget
    const rect = event.target.getBoundingClientRect()

    picker.style.position = "fixed"
    picker.style.top = `${rect.top - 50}px`
    picker.style.left = `${Math.min(rect.left, window.innerWidth - 200)}px`
    picker.classList.add("active")

    // Close picker when clicking elsewhere
    const closePicker = (e) => {
      if (!picker.contains(e.target)) {
        picker.classList.remove("active")
        document.removeEventListener("click", closePicker)
      }
    }
    setTimeout(() => document.addEventListener("click", closePicker), 0)
  }

  addReaction(event) {
    const emoji = event.target.dataset.emoji
    if (!emoji || !this.selectedMessageId) return

    this.subscription.perform("add_reaction", {
      message_id: this.selectedMessageId,
      emoji: emoji
    })

    this.reactionPickerTarget.classList.remove("active")
  }

  toggleReaction(event) {
    const emoji = event.target.dataset.emoji
    const messageEl = event.target.closest(".chat-message")
    if (!emoji || !messageEl) return

    const isRemove = event.target.classList.contains("mine")

    this.subscription.perform(isRemove ? "remove_reaction" : "add_reaction", {
      message_id: messageEl.dataset.messageId,
      emoji: emoji
    })
  }

  scrollToBottom() {
    requestAnimationFrame(() => {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    })
  }
}
